import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Users, AlertTriangle, X } from 'lucide-react';
import { incidentApi } from '../services/api';
import { Incident, CreateIncidentRequest, InjuredPerson, InjuryDetail } from '../types';
import { useLocationContext } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

const emptyInjuredPerson: InjuredPerson = {
  name: '',
  address: '',
  phone: '',
  birthDate: '',
};

const emptyInjuryDetail: InjuryDetail = {
  type: '',
  firstAid: '',
  hospitalVisitBy: '',
  helpedBy: '',
};

function padInjuredInjuryPairs(persons: InjuredPerson[], details: InjuryDetail[]): { persons: InjuredPerson[]; details: InjuryDetail[] } {
  const n = Math.max(persons.length, details.length);
  if (n === 0) return { persons: [], details: [] };
  const p = persons.map(x => ({ ...x }));
  const d = details.map(x => ({ ...x }));
  while (p.length < n) p.push({ ...emptyInjuredPerson });
  while (d.length < n) d.push({ ...emptyInjuryDetail });
  return { persons: p, details: d };
}

const MyIncidents: React.FC = () => {
  const { employee } = useAuth();
  const { locations } = useLocationContext();
  const currentUserId = employee?.id ?? 0;
  const defaultLocationId = useMemo(() => {
    const mainId = employee?.profile?.mainLocationId;
    if (mainId && locations.some(l => l.id === mainId)) return mainId;
    return locations[0]?.id ?? null;
  }, [employee?.profile?.mainLocationId, locations]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
  const [formData, setFormData] = useState<CreateIncidentRequest>({
    title: '',
    description: '',
    venue: '',
    location: '',
    locationId: null,
    dateTime: '',
    type: '',
    cctvCaught: false,
    cctvBurned: false,
    gardaPresent: false,
    photosTaken: false,
    photosAltered: false,
    securityOnDuty: '',
    managerOnDuty: '',
    status: 'Open', // Hidden but set to Open
    othersOnDuty: '',
    reportedByEmployeeId: currentUserId, // Auto-set to current user
    injuredPersons: [],
    injuryDetails: [],
    gardaInvolved: [], // Hidden section
  });

  useEffect(() => {
    if (!currentUserId) {
      setIncidents([]);
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const userIncidents = await incidentApi.getAll({ employeeId: currentUserId });
        setIncidents(userIncidents);
      } catch (error) {
        console.error('Error loading incidents:', error);
        alert('Failed to load incidents. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUserId]);

  const resetForm = () => {
    setEditingIncident(null);
    setFormData({
      title: '',
      description: '',
      venue: '',
      location: '',
      locationId: defaultLocationId,
      dateTime: '',
      type: '',
      cctvCaught: false,
      cctvBurned: false,
      gardaPresent: false,
      photosTaken: false,
      photosAltered: false,
      securityOnDuty: '',
      managerOnDuty: '',
      status: 'Open',
      othersOnDuty: '',
      reportedByEmployeeId: currentUserId,
      injuredPersons: [],
      injuryDetails: [],
      gardaInvolved: [],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    if (!formData.title.trim()) {
      alert('Title is required.');
      return;
    }
    if (locations.length > 0 && !formData.locationId) {
      alert('Please select a location.');
      return;
    }

    const locName = formData.locationId != null ? locations.find(l => l.id === formData.locationId)?.name : undefined;

    try {
      setSaving(true);
      const payload: CreateIncidentRequest = {
        ...formData,
        location: locName ?? formData.location ?? undefined,
        locationId: formData.locationId,
      };
      if (editingIncident) {
        await incidentApi.update(editingIncident.id, payload);
      } else {
        await incidentApi.create(payload);
      }

      const userIncidents = await incidentApi.getAll({ employeeId: currentUserId });
      setIncidents(userIncidents);
      resetForm();
      setShowForm(false);
    } catch (error: any) {
      console.error('Error saving incident:', error);
      const message = error?.response?.data || error?.message || 'Failed to save incident.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (incident: Incident) => {
    setEditingIncident(incident);
    const resolvedLocationId =
      incident.locationId ??
      (incident.location ? locations.find(l => l.name === incident.location)?.id : undefined) ??
      defaultLocationId;
    const personsRaw = incident.injuredPersons.length
      ? incident.injuredPersons.map(p => ({
          name: p.name,
          address: p.address || '',
          phone: p.phone || '',
          birthDate: p.birthDate || '',
        }))
      : [];
    const detailsRaw = incident.injuryDetails.length
      ? incident.injuryDetails.map(d => ({
          type: d.type,
          firstAid: d.firstAid || '',
          hospitalVisitBy: d.hospitalVisitBy || '',
          helpedBy: d.helpedBy || '',
        }))
      : [];
    const paired = padInjuredInjuryPairs(personsRaw, detailsRaw);
    setFormData({
      title: incident.title,
      description: incident.description || '',
      venue: incident.venue || '',
      location: incident.location || '',
      locationId: resolvedLocationId,
      dateTime: incident.dateTime ? incident.dateTime.substring(0, 16) : '',
      type: incident.type || '',
      cctvCaught: !!incident.cctvCaught,
      cctvBurned: !!incident.cctvBurned,
      gardaPresent: !!incident.gardaPresent,
      photosTaken: !!incident.photosTaken,
      photosAltered: !!incident.photosAltered,
      securityOnDuty: incident.securityOnDuty || '',
      managerOnDuty: incident.managerOnDuty || '',
      status: incident.status || 'Open',
      othersOnDuty: incident.othersOnDuty || '',
      reportedByEmployeeId: incident.reportedByEmployeeId,
      injuredPersons: paired.persons,
      injuryDetails: paired.details,
      gardaInvolved: [], // Hidden section
    });
    setShowForm(true);
  };

  const isReadOnly = editingIncident?.status === 'Closed';

  const updateInjuredPerson = (index: number, field: keyof InjuredPerson, value: string) => {
    if (isReadOnly) return;
    setFormData(prev => {
      const list = [...prev.injuredPersons];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, injuredPersons: list };
    });
  };

  const addPairedInjury = () => {
    if (isReadOnly) return;
    setFormData(prev => ({
      ...prev,
      injuredPersons: [...prev.injuredPersons, { ...emptyInjuredPerson }],
      injuryDetails: [...prev.injuryDetails, { ...emptyInjuryDetail }],
    }));
  };

  const removePairedInjury = (index: number) => {
    if (isReadOnly) return;
    setFormData(prev => {
      const ip = prev.injuredPersons.filter((_, i) => i !== index);
      const id = prev.injuryDetails.filter((_, i) => i !== index);
      return {
        ...prev,
        injuredPersons: ip,
        injuryDetails: id,
      };
    });
  };

  const updateInjuryDetail = (index: number, field: keyof InjuryDetail, value: string) => {
    if (isReadOnly) return;
    setFormData(prev => {
      const list = [...prev.injuryDetails];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, injuryDetails: list };
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading incidents...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">My Incidents</h1>
        {!isReadOnly && (
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Incident
          </button>
        )}
      </div>

      {/* List view */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-800">My Incidents</h2>
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <AlertTriangle className="h-4 w-4" />
            <span>{incidents.length} total</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Date/Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Venue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {incidents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-neutral-500">
                    No incidents found.
                  </td>
                </tr>
              ) : (
                incidents.map(incident => (
                  <tr key={incident.id}>
                    <td className="px-4 py-3 text-sm text-neutral-900">{incident.title}</td>
                    <td className="px-4 py-3 text-sm text-neutral-700">
                      {incident.dateTime ? formatCompanyDateTime(incident.dateTime) : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-700">{incident.venue || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-700">
                      {incident.locationRef?.name ??
                        locations.find(l => l.id === incident.locationId)?.name ??
                        incident.location ??
                        'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                          (incident.status || '').toLowerCase() === 'closed'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-orange-50 text-orange-700 border border-orange-200'
                        }`}
                      >
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {incident.status || 'Open'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => startEdit(incident)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View/Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-neutral-800">
                {editingIncident ? (isReadOnly ? 'View Incident' : 'Edit Incident') : 'Add New Incident'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="text-neutral-400 hover:text-neutral-600"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, title: e.target.value }))}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Venue</label>
                <input
                  type="text"
                  value={formData.venue || ''}
                  onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, venue: e.target.value }))}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
                <select
                  value={formData.locationId ?? ''}
                  onChange={e =>
                    !isReadOnly &&
                    setFormData(prev => ({
                      ...prev,
                      locationId: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  disabled={isReadOnly || locations.length === 0}
                  className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                >
                  {locations.length === 0 ? (
                    <option value="">No locations assigned</option>
                  ) : (
                    <>
                      <option value="">Select location</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Date &amp; Time</label>
                <input
                  type="datetime-local"
                  value={formData.dateTime || ''}
                  onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, dateTime: e.target.value }))}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
                <select
                  value={formData.type || ''}
                  onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, type: e.target.value }))}
                  disabled={isReadOnly}
                  className={`w-full px-3 py-2 border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select type</option>
                  <option value="Accidental Injury">Accidental Injury</option>
                  <option value="Violence/Physical Assault">Violence/Physical Assault</option>
                  <option value="Verbal Aggression">Verbal Aggression</option>
                  <option value="Illegal Drugs/Controlled Substance">Illegal Drugs/Controlled Substance</option>
                  <option value="Fire">Fire</option>
                  <option value="Skin Puncture/Incision/Crush/Laceration/Abrasion">Skin Puncture/Incision/Crush/Laceration/Abrasion</option>
                  <option value="Slip/Trip/Fall">Slip/Trip/Fall</option>
                  <option value="Occupational">Occupational</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
              <textarea
                rows={3}
                value={formData.description || ''}
                onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, description: e.target.value }))}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
              />
            </div>

            {/* Investigations Section */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-neutral-800 mb-3">Investigations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cctvCaught"
                    checked={formData.cctvCaught || false}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, cctvCaught: e.target.checked }))}
                    disabled={isReadOnly}
                    className={`w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500 ${isReadOnly ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="cctvCaught" className={`text-sm text-neutral-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    CCTV Caught
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="gardaPresent"
                    checked={formData.gardaPresent || false}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, gardaPresent: e.target.checked }))}
                    disabled={isReadOnly}
                    className={`w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500 ${isReadOnly ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="gardaPresent" className={`text-sm text-neutral-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    Garda Present
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="photosAltered"
                    checked={formData.photosAltered || false}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, photosAltered: e.target.checked }))}
                    disabled={isReadOnly}
                    className={`w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500 ${isReadOnly ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="photosAltered" className={`text-sm text-neutral-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    Photos Altered
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cctvBurned"
                    checked={formData.cctvBurned || false}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, cctvBurned: e.target.checked }))}
                    disabled={isReadOnly}
                    className={`w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500 ${isReadOnly ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="cctvBurned" className={`text-sm text-neutral-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    CCTV Burned
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="photosTaken"
                    checked={formData.photosTaken || false}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, photosTaken: e.target.checked }))}
                    disabled={isReadOnly}
                    className={`w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500 ${isReadOnly ? 'cursor-not-allowed' : ''}`}
                  />
                  <label htmlFor="photosTaken" className={`text-sm text-neutral-700 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    Photos Taken
                  </label>
                </div>
              </div>
            </div>

            {/* Personnel Information Section */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-neutral-800 mb-3">Personnel Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Security On Duty</label>
                  <input
                    type="text"
                    value={formData.securityOnDuty || ''}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, securityOnDuty: e.target.value }))}
                    disabled={isReadOnly}
                    placeholder="Security staff on duty"
                    className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Manager On Duty</label>
                  <input
                    type="text"
                    value={formData.managerOnDuty || ''}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, managerOnDuty: e.target.value }))}
                    disabled={isReadOnly}
                    placeholder="Manager on duty"
                    className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Others On Duty <span className="text-xs text-neutral-400">(optional)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={formData.othersOnDuty || ''}
                    onChange={e => !isReadOnly && setFormData(prev => ({ ...prev, othersOnDuty: e.target.value }))}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
            </div>

            {/* Injured person & injury details (paired entries) */}
            <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <AlertTriangle className="h-4 w-4" />
                  Injured person &amp; injury details
                </h3>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={addPairedInjury}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add entry
                  </button>
                )}
              </div>
              {formData.injuredPersons.map((person, index) => {
                const detail = formData.injuryDetails[index] ?? emptyInjuryDetail;
                return (
                  <div key={index} className="border border-neutral-100 rounded-md p-3 space-y-3 bg-neutral-50/80">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-neutral-600">Entry {index + 1}</span>
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removePairedInjury(index)}
                          className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                        >
                          Remove entry
                        </button>
                      )}
                    </div>
                    <p className="text-xs font-medium text-neutral-500">Injured person</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={person.name}
                          onChange={e => updateInjuredPerson(index, 'name', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Address</label>
                        <input
                          type="text"
                          value={person.address || ''}
                          onChange={e => updateInjuredPerson(index, 'address', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Phone</label>
                        <input
                          type="text"
                          value={person.phone || ''}
                          onChange={e => updateInjuredPerson(index, 'phone', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Birth Date</label>
                        <input
                          type="date"
                          value={person.birthDate || ''}
                          onChange={e => updateInjuredPerson(index, 'birthDate', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>
                    <p className="text-xs font-medium text-neutral-500 pt-1">Injury details</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Type</label>
                        <input
                          type="text"
                          value={detail.type}
                          onChange={e => updateInjuryDetail(index, 'type', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">First Aid</label>
                        <input
                          type="text"
                          value={detail.firstAid || ''}
                          onChange={e => updateInjuryDetail(index, 'firstAid', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Hospital Visit By</label>
                        <input
                          type="text"
                          value={detail.hospitalVisitBy || ''}
                          onChange={e => updateInjuryDetail(index, 'hospitalVisitBy', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 mb-1">Helped By</label>
                        <input
                          type="text"
                          value={detail.helpedBy || ''}
                          onChange={e => updateInjuryDetail(index, 'helpedBy', e.target.value)}
                          disabled={isReadOnly}
                          className={`w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm ${isReadOnly ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

              {!isReadOnly && (
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (editingIncident ? 'Saving...' : 'Creating...') : editingIncident ? 'Save Changes' : 'Create Incident'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyIncidents;

